import { useCallback, useRef } from "react";

type AudioCapableWindow = Window & {
  webkitAudioContext?: typeof AudioContext;
};

export interface UseAudioContextReturn {
  unlock: () => Promise<void>;
  getContext: () => AudioContext | null;
}

export function useAudioContext(): UseAudioContextReturn {
  const contextRef = useRef<AudioContext | null>(null);

  const unlock = useCallback(async () => {
    if (contextRef.current) return;

    const audioWindow = window as AudioCapableWindow;
    const AudioContextApi = window.AudioContext ?? audioWindow.webkitAudioContext;
    if (!AudioContextApi) return;

    try {
      const context = new AudioContextApi();
      context.state === "suspended" && (await context.resume());

      const buffer = context.createBuffer(1, 1, 22050);
      const source = context.createBufferSource();
      source.buffer = buffer;
      source.connect(context.destination);
      source.start(0);

      contextRef.current = context;
    } catch {
      contextRef.current = null;
    }
  }, []);

  const getContext = useCallback(() => contextRef.current, []);

  return { unlock, getContext };
}

export default useAudioContext;
